import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Shared SEO component for public marketing pages.
 * Renders <title>, <meta description>, canonical URL, Open Graph, Twitter
 * Card tags, and (optionally) JSON-LD structured data.
 *
 * Every public page (landing, pricing, kits/*) should render <SEO ... /> at
 * the top so search engines and social platforms (WhatsApp, LinkedIn,
 * Twitter) receive unique, keyword-rich metadata per URL.
 */
const SITE_URL = 'https://acbothub.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  jsonLd = null,
}) {
  const url = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="ACBotHub" />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// ─────────────────────────────────────────────────────────────
// Pre-built JSON-LD structured data blocks
// ─────────────────────────────────────────────────────────────

// Home — SoftwareApplication (helps Google show ratings/price rich snippets).
export const jsonLdSoftwareApp = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'ACBotHub',
  operatingSystem: 'Web, WhatsApp',
  applicationCategory: 'BusinessApplication',
  url: SITE_URL,
  description:
    'Plataforma para crear asistentes de WhatsApp con inteligencia artificial. Atención 24/7, agenda de citas, toma de pedidos y kits verticales para restaurantes, concesionarias, inmobiliarias y estudios jurídicos.',
  offers: [
    { '@type': 'Offer', name: 'Básico', price: '24', priceCurrency: 'USD' },
    { '@type': 'Offer', name: 'Pro',    price: '49', priceCurrency: 'USD' },
    { '@type': 'Offer', name: 'Business', price: '199', priceCurrency: 'USD' },
  ],
  publisher: { '@type': 'Organization', name: 'ACBotHub', url: SITE_URL },
};

// Per-kit Product structured data.
export function kitProduct({ name, description, path }) {
  // Derive kit slug from `path` like "/kits/garzonbot" so we can reference
  // the kit-specific OG image without adding another prop everywhere.
  const slug = path.split('/').filter(Boolean).pop();
  const image = `${SITE_URL}/og-${slug}.png`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    brand: { '@type': 'Brand', name: 'ACBotHub' },
    url: `${SITE_URL}${path}`,
    image,
    offers: {
      '@type': 'Offer',
      price: '99',
      priceCurrency: 'USD',
      priceValidUntil: '2027-12-31',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}${path}`,
    },
  };
}
